import { apiRequest, invalidateCache } from '../../shared/api/base'
import type { BriefingConfig, BriefingSource } from '../../shared/types/briefing'

// ─── Backend response types ─────────────────────────────────

interface BackendPreferences {
  userId: string
  timezone: string
  dailyResetHour: number
  idleTimeoutMinutes: number
  briefing: {
    enabled: boolean
    deliveryTime: string
    weekdaysOnly: boolean
    channels: string[]
    sources: Record<string, boolean>
  }
}

// ─── Source display metadata (frontend-only labels) ─────────

const SOURCE_META: Record<string, { label: string; description: string }> = {
  calendar: { label: 'Calendar Events', description: "Today's schedule and upcoming meetings" },
  emails: { label: 'Email Summary', description: 'Overnight emails and important messages' },
  approvals: { label: 'Pending Approvals', description: 'Actions waiting for your approval' },
  followups: { label: 'Pending Follow-ups', description: 'Things your assistant is waiting on' },
  notebook: { label: 'Notebook Insights', description: 'Birthdays, context, and reminders' },
  weather: { label: 'Weather', description: 'Current conditions and forecast for your location' },
}

// ─── Mapping helpers ────────────────────────────────────────

function mapBackendToConfig(prefs: BackendPreferences): BriefingConfig {
  const sources: Record<string, BriefingSource> = {}
  for (const [key, enabled] of Object.entries(prefs.briefing.sources)) {
    const meta = SOURCE_META[key]
    if (meta) {
      sources[key] = { ...meta, enabled }
    }
  }
  return {
    enabled: prefs.briefing.enabled,
    time: prefs.briefing.deliveryTime,
    weekdaysOnly: prefs.briefing.weekdaysOnly,
    nextRun: '',
    sources,
  }
}

// ─── API functions ──────────────────────────────────────────

export async function getBriefingConfig(): Promise<BriefingConfig> {
  const prefs = await apiRequest<BackendPreferences>('/settings/preferences')
  return mapBackendToConfig(prefs)
}

export async function updateBriefingSetting(key: string, value: unknown): Promise<void> {
  const briefingUpdate: Record<string, unknown> = {}

  if (key === 'enabled' || key === 'weekdaysOnly') {
    briefingUpdate[key] = value
  } else if (key === 'time') {
    briefingUpdate.deliveryTime = value
  } else if (key.startsWith('sources.')) {
    // e.g. "sources.calendar.enabled" -> { sources: { calendar: true } }
    const parts = key.split('.')
    const sourceKey = parts[1]
    if (sourceKey) {
      briefingUpdate.sources = { [sourceKey]: value }
    }
  }

  await apiRequest('/settings/preferences', {
    method: 'PATCH',
    body: JSON.stringify({ briefing: briefingUpdate }),
  })
  invalidateCache('/settings/preferences')
}
